import AppFormContainer from "@/components/AppFormContainer";
import A11yOptions from "@/components/comm-generator/A11yOptions";
import ResultPanel from "@/components/comm-generator/ResultPanel";
import { FileInput } from "@/components/lesson-lab/FileInput";
import SideTitle from "@/components/SideTitle";
import SplitPane from "@/components/SplitPane";
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToolSubmission } from "@/lib/queries/tools/useToolSubmission"; // legacy (non-SSE)
import { useToolSubmissionSSE } from "@/lib/queries/tools/useToolSubmissionSSE";
import FloatingDocStatuses from "@/components/results/FloatingDocStatuses";
import { buildDocStatuses } from "@/components/results/buildDocStatuses";
import { useIsMobile } from "@/hooks/use-mobile";
import { PaintBrushBroad } from "@phosphor-icons/react";
import { useStytchSession } from "@stytch/react";
import { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import useSessionStorage from "@/lib/hooks/useSessionStorage";
import { filterFormPreferences } from "@/lib/utils/filterFormPreferences";
import { AGE_GROUPS, LEARNING_REQUIREMENTS } from "@/lib/types/comms";
import { LanguageSelector } from "@/components/lesson-lab/LanguageSelector";
import useUserSettings from "@/lib/queries/user/useUserSettings";
import { DEFAULT_RESULT_LANGUAGE } from "@/lib/constants/languages";
import { resolveInitialToolLanguage } from "@/lib/utils/language";
import { useTranslation } from "react-i18next";
import { useA11ySettings } from "@/lib/hooks/useA11ySettings";
import TutorialBox, { useTutorialState } from "@/components/TutorialBox";
import { GuidedTour, TourStep } from "@/components/GuidedTour";

function ResourceCustomiser() {
  const { t } = useTranslation("resourceCustomiser");
  const isMobile = useIsMobile();
  const [storedIntermediate, setStoredIntermediate] = useState<string | null>(
    null
  );
  const [lastFileUrls, setLastFileUrls] = useState<string[] | null>(null);
  const { session, fromCache } = useStytchSession();
  const { useGetUserSettings } = useUserSettings();
  const { language: a11yLanguage } = useA11ySettings();
  const { data: userSettings } = useGetUserSettings({
    enabled: !!session && !fromCache,
  });
  const initialLanguage = resolveInitialToolLanguage({
    a11yLanguage,
    userSettings,
    fallback: DEFAULT_RESULT_LANGUAGE,
  });

  const form = useForm({
    defaultValues: {
      files: null,
      fileUrls: [],
      learning_requirement: LEARNING_REQUIREMENTS.UNSET,
      age: AGE_GROUPS.UNSET,
      language: initialLanguage,
      showAccessibilityOptions: false,
    },
  });

  // Session storage and save preferences functionality

  const [savePreferencesEnabled, setSavePreferencesEnabled] = useState(false);
  const [getPreferences, setPreferences] = useSessionStorage(
    "resource-customiser-preferences",
    {}
  );

  useEffect(() => {
    const subscription = form.watch((value) => {
      if (form.formState.isDirty && value) {
        if (savePreferencesEnabled) {
          const preferences = filterFormPreferences(value);
          setPreferences(preferences);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form, savePreferencesEnabled, setPreferences]);

  useEffect(() => {
    if (getPreferences && Object.keys(getPreferences).length > 0) {
      form.reset({
        ...form.getValues(),
        ...getPreferences,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tourOpen, setTourOpen] = useState(false);
  const tutorialState = useTutorialState("resourcecustomiser-tutorial");
  const tourSteps: TourStep[] = useMemo(
    () => [
      {
        selector: "#side-title",
        title: t("tutorial.1.title"),
        content: t("tutorial.1.body", { ns: "resourceCustomiser" }),
      },
      {
        selector: "#tour-file-input",
        title: t("tutorial.2.title"),
        content: t("tutorial.2.body", { ns: "resourceCustomiser" }),
      },
      {
        selector: "#tour-language-container",
        title: t("tutorial.3.title"),
        content: t("tutorial.3.body", { ns: "resourceCustomiser" }),
      },
      {
        selector: "#tour-generate",
        title: t("tutorial.4.title"),
        content: t("tutorial.4.body", { ns: "resourceCustomiser" }),
      },
    ],
    [t]
  );

  // Update language after a11y or user settings change if user hasn't modified it
  useEffect(() => {
    const desired = resolveInitialToolLanguage({
      a11yLanguage,
      userSettings,
      fallback: DEFAULT_RESULT_LANGUAGE,
    });
    const current = form.getValues("language");
    const languageDirty = (form.formState.dirtyFields as any)?.language;
    if (!languageDirty && current !== desired) {
      form.setValue("language", desired, { shouldDirty: false });
    }
  }, [a11yLanguage, userSettings, form, initialLanguage]);

  const USE_SSE = true;
  const sse = USE_SSE
    ? useToolSubmissionSSE("resourcecustomiser")
    : (null as any);
  const mutation = !USE_SSE
    ? useToolSubmission("resourcecustomiser")
    : (null as any);
  const isPending = USE_SSE ? sse.isStreaming : Boolean(mutation?.isPending);
  const isSuccess = USE_SSE
    ? Boolean(sse.final || sse.latestOutput)
    : Boolean(mutation?.data);
  const data = USE_SSE ? sse.final : mutation?.data;

  const onSubmit = form.handleSubmit(
    ({ fileUrls, age, language, learning_requirement }) => {
      const filesChanged =
        lastFileUrls !== null &&
        JSON.stringify(lastFileUrls) !== JSON.stringify(fileUrls);
      const intermediateValue = filesChanged ? null : storedIntermediate;

      const payload = {
        subject: "",
        type: [],
        requirements: {
          age:
            age === AGE_GROUPS.UNSET
              ? null
              : t("age", {
                  lng: "en",
                  ns: "common",
                }),
          language:
            t(language, { lng: "en", ns: "common" }) || DEFAULT_RESULT_LANGUAGE,
          learning_requirement:
            learning_requirement === LEARNING_REQUIREMENTS.UNSET
              ? null
              : t(learning_requirement, { lng: "en", ns: "common" }),
        },
        regenerate: {
          intermediate: intermediateValue || null,
          feedback: null,
        },
        attachments: fileUrls,
      } as const;

      if (USE_SSE) {
        sse.start(payload as any);
      } else {
        (mutation as any).mutate(payload as any);
      }

      setLastFileUrls(fileUrls);
    }
  );

  useEffect(() => {
    if (isSuccess && data?.intermediate) {
      setStoredIntermediate(data.intermediate);
    }
  }, [isSuccess, data?.intermediate]);

  return (
    <>
      <SplitPane
        showResultPanel={
          USE_SSE
            ? Boolean(sse.final || sse.latestOutput)
            : Boolean(mutation?.data)
        }
        key={
          isSuccess ? "resource-customiser-success" : "resource-customiser-form"
        }
      >
        <AppFormContainer
          form={form}
          onSubmit={onSubmit}
          isSuccess={isSuccess}
          isPending={isPending}
          savePreferences={{
            enabled: savePreferencesEnabled,
            onToggle: setSavePreferencesEnabled,
          }}
        >
          <SideTitle
            title={t("title")}
            intro={t("intro")}
            icon={PaintBrushBroad}
            showHelpIndicator={tutorialState.dismissed}
            onHelpClick={tutorialState.reopen}
          />
          <p className="text-sm text-muted-foreground -mt-2 mb-4">
            {t("performanceNotice")}
          </p>
          <TutorialBox
            storageKey="resourcecustomiser-tutorial"
            title={t("common:quickGuide", { defaultValue: "Quick guide" })}
            message={
              t("tutorial.blurb", {
                ns: "resourceCustomiser",
                defaultValue: "Short tour of Resource Customiser.",
              }) || "Short tour of Resource Customiser."
            }
            onStartTour={() => setTourOpen(true)}
            tutorialState={tutorialState}
          />
          <GuidedTour
            open={tourOpen}
            steps={tourSteps}
            storageKey="resourcecustomiser-tutorial"
            onClose={() => setTourOpen(false)}
          />

          <FormField
            control={form.control}
            name="files"
            render={({ field }) => (
              <FormItem id="tour-file-input">
                <FormLabel>{t("form.fileUpload.label")}</FormLabel>
                <FormDescription className="mb-4 !text-content !text-light-grey">
                  {t("form.fileUpload.description")}
                </FormDescription>
                <FileInput
                  className="border-transparent rounded-md"
                  type="file"
                  accept="abw,csv,doc,docm,docx,dot,dotx,dps,et,hwp,lwp,md,odp,ods,odt,pdf,pps,ppsx,ppt,pptm,pptx,rst,rtf,txt,wpd,wps,xls,xlsm,xlsx,xps,zabw"
                  multiple
                  onFilesChange={(files) => field.onChange(files)}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
                <TooltipProvider>
                  <FormDescription className="mt-2 !text-content !text-light-grey">
                    {t("form.fileUpload.supportedFiles.text")}{" "}
                    <Tooltip>
                      <TooltipTrigger
                        className="underline text-primary decoration-dashed"
                        type="button"
                      >
                        {t("form.fileUpload.supportedFiles.linkText")}
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="">
                          <ul className="grid grid-cols-3 gap-x-4">
                            <li>.abw</li>
                            <li>.csv</li>
                            <li>.doc</li>
                            <li>.docm</li>
                            <li>.docx</li>
                            <li>.dot</li>
                            <li>.dotx</li>
                            <li>.dps</li>
                            <li>.et</li>
                            <li>.hwp</li>
                            <li>.lwp</li>
                            <li>.md</li>
                            <li>.odp</li>
                            <li>.ods</li>
                            <li>.odt</li>
                            <li>.pdf</li>
                            <li>.pps</li>
                            <li>.ppsx</li>
                            <li>.ppt</li>
                            <li>.pptm</li>
                            <li>.pptx</li>
                            <li>.rst</li>
                            <li>.rtf</li>
                            <li>.txt</li>
                            <li>.wpd</li>
                            <li>.wps</li>
                            <li>.xls</li>
                            <li>.xlsm</li>
                            <li>.xlsx</li>
                            <li>.xps</li>
                            <li>.zabw</li>
                          </ul>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </FormDescription>
                </TooltipProvider>
              </FormItem>
            )}
          />
          <div className="flex flex-col gap-4" id="tour-language-container">
            <LanguageSelector name="language" />

            <FormLabel className="mt-2 -mb-2">
              {t("form.accessibility.label")}
            </FormLabel>
            <A11yOptions requirementRequired={false} ageRequired={false} />
          </div>
        </AppFormContainer>
        <ResultPanel
          appResult="resource-customiser"
          isLoading={isPending}
          result={
            USE_SSE
              ? data?.results
                ? data.results
                : sse.latestOutput
                  ? [sse.latestOutput]
                  : undefined
              : mutation?.data?.results
          }
          toolResponse={USE_SSE ? data : mutation?.data}
          sseEvents={USE_SSE ? sse.events : undefined}
          sseError={USE_SSE ? sse.error : (mutation?.error as any)}
          loadingStatuses={
            USE_SSE
              ? buildDocStatuses({
                  expectedDocs: [
                    {
                      type: "Document",
                      language:
                        (form.getValues("language") as string) ||
                        DEFAULT_RESULT_LANGUAGE,
                    },
                  ],
                  sseEvents: sse.events,
                  results:
                    (data?.results as any) ||
                    (sse.latestOutput ? [sse.latestOutput] : []),
                  isStreaming: sse.isStreaming,
                  isLoading: isPending,
                })
              : undefined
          }
          loadingStage={USE_SSE ? sse.stage : undefined}
          loadingProgress={USE_SSE ? sse.progress : undefined}
          onCancel={USE_SSE ? sse.abort : undefined}
        />
      </SplitPane>
      {USE_SSE &&
        (() => {
          const docStatuses = buildDocStatuses({
            expectedDocs: [
              {
                type: "Document",
                language:
                  (form.getValues("language") as string) ||
                  DEFAULT_RESULT_LANGUAGE,
              },
            ],
            sseEvents: sse.events,
            results:
              (data?.results as any) ||
              (sse.latestOutput ? [sse.latestOutput] : []),
            isStreaming: sse.isStreaming,
            isLoading: isPending,
          });
          const hasReady = docStatuses.some((s) => s.status === "ready");
          const showFloating =
            Boolean(sse.isStreaming) &&
            (isMobile || hasReady) &&
            docStatuses.length > 0 &&
            sse.stage !== "done" &&
            !sse.error;
          return (
            <FloatingDocStatuses
              visible={showFloating}
              statuses={docStatuses}
              stage={sse.stage}
              progress={sse.progress}
              onClose={() => {}}
              onCancel={sse.abort}
            />
          );
        })()}
    </>
  );
}

export default ResourceCustomiser;
